import { takeLatest, call, put, select, take } from 'redux-saga/effects';
import USER_TYPES from './types';
import userActions from './actions';
import notificationActions from '../Notifications/actions';
import { sagaWrapper } from 'src/helpers';
import Service from 'src/service';
import { type IResponseMetadata, type IResponse } from 'src/models';

const User = new Service('users');

function* getAll({ payload }: any) {
	const { page, pageSize, type } = payload;
	const response: IResponseMetadata = yield call(
		User.getPath,
		`type/${type}?page=${page}&pageSize=${pageSize}`,
	);
	yield put(userActions.setAll(response.data.data, response.data.metadata));
}

function* getByInstitution({ payload }: any) {
	const { page, pageSize, type, institutionId } = payload;
	const response: IResponseMetadata = yield call(
		User.getPath,
		`institution/${institutionId}?type=${type}&page=${page}&pageSize=${pageSize}`,
	);
	yield put(userActions.setAll(response.data.data, response.data.metadata));
}

function* getAllByInstitution({ payload }: any) {
	const response: IResponseMetadata = yield call(
		User.getPath,
		`institution/${payload.institutionId}?type=User`,
	);
	yield put(userActions.setAllByInstitution(response.data.data));
}

function* getOne({ payload }: any) {
	const response: IResponse = yield call(User.getOne, payload.id);
	yield put(userActions.setOne(response.data));
}

function* create({ payload }: any) {
	yield call(User.create, payload.user);
	yield put(notificationActions.add('success', 'Usuario creado'));
	yield call(getUsersStore);
}

function* update({ payload }: any) {
	yield call(User.update, payload.user);
	yield put(notificationActions.add('success', 'Usuario actualizado'));
	yield call(getUsersStore);
}

function* remove({ payload }: any) {
	yield call(User.delete, payload.id);
	yield put(notificationActions.add('success', 'Usuario Eliminado'));
	yield call(getUsersStore);
}

function* getUsersStore() {
	const usersState: {
		type: string;
		page: number;
		pageSize: number;
		institutionId: number;
	} = yield select((state: any) => state.users);
	if (usersState.type === 'Admin')
		yield put(
			userActions.getAll(
				usersState.type,
				usersState.page,
				usersState.pageSize,
			),
		);
	else
		yield put(
			userActions.getByInstitution(
				usersState.institutionId,
				usersState.type,
				usersState.page,
				usersState.pageSize,
			),
		);
}

function* bulkUpload({ payload }: any) {
	const response: IResponse = yield call(
		User.createPath,
		'bulk',
		payload.formData,
	);
	yield put(userActions.set('isUploadingBulk', false));
	yield put(notificationActions.add('success', `Archivo procesado`));
	yield put(userActions.setBulkUploadSummary(response.data));
}

function* downloadLayout() {
	yield call(User.downloadLayout);
	yield put(userActions.set('isDownloadingLayout', false));
}

function* getCertificates({ payload }: any) {
	const { institutionId, courseId, year, page, pageSize } = payload;
	const response: IResponseMetadata = yield call(
		User.getPath,
		`certificates/${institutionId}/${courseId}/${year}?page=${page}&pageSize=${pageSize}`,
	);
	yield put(
		userActions.setCertificates(response.data.data, response.data.metadata),
	);
}

function* downloadCertificates({ payload }: any) {
	const { institutionId, courseId, year, institutionName } = payload;
	yield call(
		User.download,
		`download_certificates/${institutionId}/${courseId}/${year}?institutionName=${institutionName}`,
		`Certificados - ${institutionName} - ${year}.zip`,
		{},
		{ disableMimeType: true }
	);
	yield put(userActions.set('isDownloadingCertificates', false));
}

export default function* index() {
	yield takeLatest(
		USER_TYPES.GET_ALL,
		sagaWrapper(getAll, userActions.setAll([])),
	);
	yield takeLatest(
		USER_TYPES.GET_ONE,
		sagaWrapper(getOne, userActions.setOne({})),
	);
	yield takeLatest(
		USER_TYPES.GET_BY_INSTITUTION,
		sagaWrapper(getByInstitution, userActions.setAll([])),
	);
	yield takeLatest(
		USER_TYPES.GET_ALL_BY_INSTITUTION,
		sagaWrapper(getAllByInstitution, userActions.setAll([])),
	);
	yield takeLatest(USER_TYPES.CREATE, sagaWrapper(create));
	yield takeLatest(USER_TYPES.UPDATE, sagaWrapper(update));
	yield takeLatest(USER_TYPES.REMOVE, sagaWrapper(remove));
	yield takeLatest(
		USER_TYPES.BULK_UPLOAD,
		sagaWrapper(bulkUpload, userActions.set('isUploadingBulk', false)),
	);
	yield takeLatest(
		USER_TYPES.DOWNLOAD_LAYOUT,
		sagaWrapper(
			downloadLayout,
			userActions.set('isDownloadingLayout', false),
		),
	);
	yield takeLatest(
		USER_TYPES.GET_CERTIFICATES,
		sagaWrapper(
			getCertificates,
			userActions.setCertificates([], { totalPages: 0, totalResults: 0 }),
		),
	);
	yield takeLatest(
		USER_TYPES.DOWNLOAD_CERTIFICATES,
		sagaWrapper(downloadCertificates),
	);
}
